// World renderer: absolutely-positioned notes on an infinite,
// camera-transformed plane. Positions are world coordinates of the
// note's centre; the camera is applied to #world as one transform.

import type { BoardConfig, Note } from "@ts/core/types.ts";
import { inkColor, stickerArt } from "@ts/core/types.ts";
import type { Camera } from "@ts/board/camera.ts";
import { noteHash } from "@ts/board/placement.ts";
import { el } from "../util/dom.ts";

let world: HTMLElement;
const noteEls = new Map<string, HTMLElement>();
let ghostEl: HTMLElement | null = null;

export function initWorld(worldEl: HTMLElement): void {
  world = worldEl;
}

export function applyCamera(c: Camera): void {
  world.style.transform = `translate(${c.x}px, ${c.y}px) scale(${c.z})`;
}

export interface GhostData {
  message: string;
  name: string | null;
  paper: string;
  font: string;
  ink: string;
  rotation: number;
  pos_x: number;
  pos_y: number;
  photo_url?: string | null;
  audio_url?: string | null;
}

function positionEl(node: HTMLElement, x: number, y: number, rotation: number): void {
  node.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rotation}deg)`;
}

/** Build a note's DOM (shared by board, ghost, and enlarge view). */
export function buildNoteBody(note: GhostData & Partial<Note>): HTMLElement {
  if (note.kind === "sticker") {
    const node = el("article", "note sticker");
    const art = stickerArt(note.message);
    if (art) {
      const img = el("img", "sticker-art");
      img.src = art;
      img.alt = "sticker";
      img.draggable = false;
      node.appendChild(img);
    } else {
      node.appendChild(el("span", "sticker-emoji", note.message));
    }
    return node;
  }

  const paper = note.photo_url ? "polaroid" : note.paper;
  const node = el("article", `note paper-${paper} font-${note.font}`);
  node.style.setProperty("--note-ink", inkColor(note.ink));

  const pin = el("span", `pin pin-${(note as Note).pin || "brass"}`);
  node.appendChild(pin);

  if (note.photo_url) {
    const img = el("img", "note-photo");
    img.src = note.photo_url;
    img.alt = "a photo pinned to the board";
    img.loading = "lazy";
    img.draggable = false;
    node.appendChild(img);
  }

  if (note.message) node.appendChild(el("p", "note-text", note.message));

  if (note.audio_url) {
    const btn = el("button", "tape-btn", "▶ voice note");
    btn.type = "button";
    const audio = new Audio(note.audio_url);
    audio.onended = () => (btn.textContent = "▶ voice note");
    btn.onclick = (e) => {
      e.stopPropagation();
      if (audio.paused) {
        void audio.play();
        btn.textContent = "◼ playing…";
      } else {
        audio.pause();
        audio.currentTime = 0;
        btn.textContent = "▶ voice note";
      }
    };
    node.appendChild(btn);
  }

  node.appendChild(
    el("footer", "note-name", note.name ? `— ${note.name}` : "— someone who was there")
  );
  return node;
}

export interface UpsertOpts {
  mine?: boolean;
  animate?: boolean;
  /** Own stickers get a tiny corner ×; tapping it calls this. */
  onRemove?: () => void;
}

export function upsertNote(note: Note, opts: UpsertOpts = {}): HTMLElement {
  const node = buildNoteBody(note as GhostData & Note);
  node.dataset.noteId = note.id;
  if (opts.mine) node.classList.add("mine");
  if (opts.mine && note.kind === "sticker" && opts.onRemove) {
    // The × stays hidden until the owner taps the sticker (see main.ts
    // onTapNote → the .peelable class), so the board isn't littered with ×s.
    const x = el("button", "sticker-x", "×");
    x.type = "button";
    x.setAttribute("aria-label", "peel this sticker off");
    x.onclick = (e) => {
      e.stopPropagation();
      opts.onRemove?.();
    };
    node.appendChild(x);
  }
  if (noteHash(note.id) % 3 === 0) node.classList.add("lifted");
  if (opts.animate) node.classList.add("settling");
  positionEl(node, note.pos_x ?? 0, note.pos_y ?? 0, note.rotation);

  const prev = noteEls.get(note.id);
  if (prev) prev.replaceWith(node);
  else world.appendChild(node);
  noteEls.set(note.id, node);
  return node;
}

export function moveNote(id: string, x: number, y: number, rotation: number): void {
  const node = noteEls.get(id);
  if (node) positionEl(node, x, y, rotation);
}

export function removeNote(id: string): void {
  noteEls.get(id)?.remove();
  noteEls.delete(id);
}

export function noteElOf(id: string): HTMLElement | undefined {
  return noteEls.get(id);
}

export function setGhost(ghost: GhostData | null): void {
  ghostEl?.remove();
  ghostEl = null;
  if (!ghost) return;
  ghostEl = buildNoteBody(ghost);
  ghostEl.classList.add("ghost");
  positionEl(ghostEl, ghost.pos_x, ghost.pos_y, ghost.rotation);
  world.appendChild(ghostEl);
}

export interface MastheadRefs {
  freezeLine: HTMLElement;
  capLine: HTMLElement;
}

let mastheadEl: HTMLElement | null = null;

/** Masthead bounding rect in world coords (centre-based). */
export function mastheadRect(): { x: number; y: number; w: number; h: number } {
  return {
    x: 0,
    y: 0,
    w: mastheadEl?.offsetWidth ?? 370,
    h: mastheadEl?.offsetHeight ?? 420,
  };
}

/** Rendered size of a note in world units (untransformed CSS px). */
export function noteSize(id: string): { w: number; h: number } {
  const node = noteEls.get(id);
  return { w: node?.offsetWidth ?? 230, h: node?.offsetHeight ?? 200 };
}

/** The masthead is itself a big sheet pinned at the world origin. */
export function renderMasthead(cfg: BoardConfig): MastheadRefs {
  const sheet = el("section", "note paper-cream masthead");
  mastheadEl = sheet;
  sheet.appendChild(el("span", "pin pin-brass"));
  sheet.appendChild(el("h1", "masthead-title", cfg.title));
  sheet.appendChild(el("p", "masthead-tagline", cfg.tagline));
  sheet.appendChild(el("p", "masthead-subtitle", cfg.subtitle));
  sheet.appendChild(el("p", "masthead-intro"));
  if (cfg.slamLine) sheet.appendChild(el("p", "masthead-slam", cfg.slamLine));
  const freezeLine = el("p", "masthead-freeze");
  const capLine = el("p", "masthead-cap");
  capLine.hidden = true;
  sheet.appendChild(freezeLine);
  sheet.appendChild(capLine);
  positionEl(sheet, 0, 0, -0.8);
  world.appendChild(sheet);
  return { freezeLine, capLine };
}

export function setMastheadIntro(sheetIntro: string): void {
  const p = world.querySelector<HTMLElement>(".masthead-intro");
  if (p) p.textContent = sheetIntro;
}
